import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/useApp';
import { motion } from 'framer-motion';
import { ArrowRight, Languages } from 'lucide-react';


const WelcomeScreen = () => {
  const navigate = useNavigate();
  const { language, setLanguage } = useApp();

  const translations = {
    english: {
      nyay: 'Nyay (न्याय) = Justice',
      sahayak: 'Sahayak (सहायक) = Helper',
      description: 'Connecting clients with qualified lawyers for instant legal consultation at reasonable prices. Get legal help when you need it most.',
      chooseLanguage: 'Choose Your Language',
      english: 'English',
      hindi: 'हिंदी (Hindi)',
      getStarted: 'Get Started',
      tagline: 'Justice for all, help for everyone'
    },
    hindi: {
      nyay: 'न्याय (Justice) = न्याय',
      sahayak: 'सहायक (Helper) = सहायक',
      description: 'ग्राहकों को योग्य वकीलों से तुरंत कानूनी परामर्श के लिए जोड़ना उचित कीमतों पर। जब आपको सबसे ज्यादा जरूरत हो तब कानूनी मदद प्राप्त करें।',
      chooseLanguage: 'अपनी भाषा चुनें',
      english: 'English',
      hindi: 'हिंदी (Hindi)',
      getStarted: 'शुरू करें',
      tagline: 'सभी के लिए न्याय, सभी के लिए मदद'
    }
  };

  const t = translations[language] || translations.english;

  const handleLanguageChange = (selectedLanguage) => {
    setLanguage(selectedLanguage);
  };

  const handleGetStarted = () => {
    navigate('/intro/statistics');
  };

  return (
    <div className="min-h-screen w-full bg-white dark:bg-gray-900 flex flex-col justify-center items-center p-4 relative overflow-hidden">
      {/* Background blur effect */}
      <div className="absolute inset-0 bg-white/50 dark:bg-gray-900/50 backdrop-blur-sm"></div>
      
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="relative z-10 text-center w-full max-w-md mx-auto"
      >
        {/* Logo */}
        <motion.div
          initial={{ scale: 0.8 }}
          animate={{ scale: 1 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="mb-8"
        >
          <div className="inline-flex items-center justify-center mb-4">
            <span role="img" aria-label="gold ribbon" className="text-4xl sm:text-5xl lg:text-6xl drop-shadow-[0_0_10px_rgba(234,179,8,0.75)]">
              🎗
            </span>
          </div>
        </motion.div>

        {/* App Name */}
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-4"
        >
          NyaySahayak
        </motion.h1>

        {/* Definitions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mb-6"
        >
          <p className="text-lg sm:text-xl text-purple-600 dark:text-purple-400 font-medium mb-2">
            {t.nyay}
          </p>
          <p className="text-lg sm:text-xl text-purple-600 dark:text-purple-400 font-medium">
            {t.sahayak}
          </p>
        </motion.div>

        {/* Description Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="mb-8"
        >
          <div className="card">
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
              {t.description}
            </p>
          </div>
        </motion.div>

        {/* Language Selection */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="mb-8"
        >
          <div className="card">
            <div className="flex flex-col items-center">
              <h3 className="text-gray-800 dark:text-gray-200 font-medium mb-4 flex items-center gap-2">
                <Languages className="w-5 h-5" />
                {t.chooseLanguage}
              </h3>
              <div className="grid grid-cols-2 gap-3 w-full">
                <button
                  onClick={() => handleLanguageChange('english')}
                  className={`px-4 py-3 rounded-xl flex items-center justify-center transition-all ${language === 'english' ? 'bg-purple-100 dark:bg-purple-900 text-purple-700 dark:text-purple-300 font-medium' : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'}`}
                >
                  {t.english}
                </button>
                <button
                  onClick={() => handleLanguageChange('hindi')}
                  className={`px-4 py-3 rounded-xl flex items-center justify-center transition-all ${language === 'hindi' ? 'bg-purple-100 dark:bg-purple-900 text-purple-700 dark:text-purple-300 font-medium' : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'}`}
                >
                  {t.hindi}
                </button>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Get Started Button */}
        <motion.button
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.7 }}
          onClick={handleGetStarted}
          className="w-full bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white font-bold py-4 px-6 rounded-2xl transition-all duration-200 flex items-center justify-center gap-3 shadow-xl hover:shadow-2xl transform hover:scale-105 mb-4"
        >
          <span className="text-base sm:text-lg">{t.getStarted}</span>
          <ArrowRight className="w-5 h-5" />
        </motion.button>

        {/* Temporary logout button for testing */}
        <motion.button
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.8 }}
          onClick={() => {
            localStorage.clear();
            sessionStorage.clear();
            window.location.reload();
          }}
          className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold py-3 px-6 rounded-xl transition-all duration-200 flex items-center justify-center gap-2 shadow-lg hover:shadow-xl"
        >
          🔄 Clear Session & Restart
        </motion.button>

        {/* Tagline */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="text-sm text-gray-500 dark:text-gray-400 mt-6"
        >
          {t.tagline}
        </motion.p>
      </motion.div>
    </div>
  );
};

export default WelcomeScreen;